/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import type { Role, Permission } from '../types';

/**
 * Role-based permissions matrix.
 * Defines what each role can do across all resources.
 */
const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  super_admin: [
    {
      resource: '*',
      actions: ['read', 'create', 'update', 'delete', 'manage'],
    },
  ],
  admin: [
    { resource: 'dashboard', actions: ['read'] },
    { resource: 'demo_module', actions: ['read'] },
    {
      resource: 'demand_sources',
      actions: ['read', 'create', 'update', 'delete'],
    },
    { resource: 'users', actions: ['read', 'create', 'update', 'delete'] },
    { resource: 'config', actions: ['read', 'update'] },
    { resource: 'analytics', actions: ['read'] },
    { resource: 'schedules', actions: ['read', 'create', 'update', 'delete'] },
    { resource: 'services', actions: ['read', 'manage'] },
    { resource: 'audit', actions: ['read'] },
    {
      resource: 'req_strategies',
      actions: ['read', 'create', 'update', 'delete'],
    },
    {
      resource: 'opt_strategies',
      actions: ['read', 'create', 'update', 'delete'],
    },
    {
      resource: 'rule_sets',
      actions: ['read', 'create', 'update', 'delete'],
    },
    {
      resource: 'guidance',
      actions: ['read', 'create', 'update', 'delete'],
    },
    {
      resource: 'strategy_selectors',
      actions: ['read', 'update'],
    },
    { resource: 'system_backup', actions: ['manage'] },
    { resource: 'api_explorer', actions: ['read'] },
    { resource: 'metrics_export', actions: ['read'] },
    { resource: 'metrics_console', actions: ['read'] },
    // Cancelling a session is admin-only, matching the API (`session-management.service.ts`,
    // `isAdminLike`); ad_ops reads sessions and does not cancel them (#932, Todd 2026-09-29).
    { resource: 'sessions', actions: ['read', 'manage'] },
    { resource: 'request_tracer', actions: ['read'] },
    {
      resource: 'pixels',
      actions: ['read', 'create', 'update', 'delete'],
    },
    { resource: 'field_catalog', actions: ['read'] },
    { resource: 'pipeline_diagnostics', actions: ['read'] },
    {
      resource: 'expression_library',
      actions: ['read', 'create', 'update', 'delete'],
    },
    { resource: 'error_logs', actions: ['read'] },
    {
      resource: 'alert_subscriptions',
      actions: ['read', 'create', 'update', 'delete'],
    },
  ],
  ad_ops: [
    { resource: 'dashboard', actions: ['read'] },
    { resource: 'demo_module', actions: ['read'] },
    // `delete` follows who manages the source, as edit does (Todd, 2026-10-05); the server's stamp decides which.
    { resource: 'demand_sources', actions: ['read', 'create', 'update', 'delete'] },
    { resource: 'analytics', actions: ['read'] },
    { resource: 'schedules', actions: ['read'] },
    { resource: 'config', actions: ['read'] },
    { resource: 'services', actions: ['read'] },
    { resource: 'users', actions: ['read'] },
    { resource: 'audit', actions: ['read'] },
    {
      resource: 'req_strategies',
      actions: ['read', 'create', 'update', 'delete'],
    },
    {
      resource: 'opt_strategies',
      actions: ['read', 'create', 'update', 'delete'],
    },
    {
      resource: 'rule_sets',
      actions: ['read', 'create', 'update', 'delete'],
    },
    {
      resource: 'guidance',
      actions: ['read', 'create', 'update', 'delete'],
    },
    {
      resource: 'strategy_selectors',
      actions: ['read', 'update'],
    },
    { resource: 'api_explorer', actions: ['read'] },
    { resource: 'metrics_export', actions: ['read'] },
    { resource: 'metrics_console', actions: ['read'] },
    { resource: 'sessions', actions: ['read'] },
    { resource: 'request_tracer', actions: ['read'] },
    {
      resource: 'pixels',
      actions: ['read', 'create', 'update', 'delete'],
    },
    { resource: 'field_catalog', actions: ['read'] },
    { resource: 'pipeline_diagnostics', actions: ['read'] },
    {
      resource: 'expression_library',
      actions: ['read', 'create', 'update', 'delete'],
    },
    { resource: 'error_logs', actions: ['read'] },
    {
      resource: 'alert_subscriptions',
      actions: ['read', 'create', 'update', 'delete'],
    },
  ],
  sales: [
    { resource: 'dashboard', actions: ['read'] },
    { resource: 'demo_module', actions: ['read'] },
    { resource: 'demand_sources', actions: ['read', 'create'] },
    { resource: 'analytics', actions: ['read'] },
    { resource: 'schedules', actions: ['read'] },
    { resource: 'services', actions: ['read'] },
    { resource: 'users', actions: ['read'] },
    { resource: 'req_strategies', actions: ['read'] },
    { resource: 'opt_strategies', actions: ['read'] },
    { resource: 'rule_sets', actions: ['read'] },
    { resource: 'guidance', actions: ['read'] },
    { resource: 'strategy_selectors', actions: ['read'] },
    { resource: 'metrics_export', actions: ['read'] },
    { resource: 'pixels', actions: ['read'] },
  ],
  viewer: [
    { resource: 'dashboard', actions: ['read'] },
    { resource: 'demo_module', actions: ['read'] },
    { resource: 'demand_sources', actions: ['read'] },
    { resource: 'analytics', actions: ['read'] },
    { resource: 'schedules', actions: ['read'] },
    { resource: 'services', actions: ['read'] },
    { resource: 'users', actions: ['read'] },
    { resource: 'req_strategies', actions: ['read'] },
    { resource: 'opt_strategies', actions: ['read'] },
    { resource: 'rule_sets', actions: ['read'] },
    { resource: 'guidance', actions: ['read'] },
    { resource: 'strategy_selectors', actions: ['read'] },
    { resource: 'metrics_export', actions: ['read'] },
    { resource: 'pixels', actions: ['read'] },
  ],
  demo_user: [
    { resource: 'dashboard', actions: ['read'] },
    { resource: 'demo_module', actions: ['read', 'create', 'update'] },
    {
      resource: 'demand_sources',
      actions: ['read', 'create', 'update', 'delete'],
    },
    { resource: 'analytics', actions: ['read'] },
    { resource: 'metrics_console', actions: ['read'] },
    { resource: 'req_strategies', actions: ['read'] },
    { resource: 'opt_strategies', actions: ['read'] },
    { resource: 'rule_sets', actions: ['read'] },
    { resource: 'guidance', actions: ['read'] },
    { resource: 'strategy_selectors', actions: ['read'] },
    { resource: 'pixels', actions: ['read'] },
    {
      resource: 'expression_library',
      actions: ['read'],
    },
  ],
};

export type Action = 'read' | 'create' | 'update' | 'delete' | 'manage';

/**
 * Check if a role has permission to perform an action on a resource.
 */
export function hasPermission(role: Role | undefined, resource: string, action: Action): boolean {
  if (!role) return false;

  const perms = ROLE_PERMISSIONS[role] ?? [];

  for (const perm of perms) {
    if (perm.resource === '*' || perm.resource === resource) {
      if (perm.actions.includes('manage') || perm.actions.includes(action)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Get all permissions for a role.
 */
export function getRolePermissions(role: Role): Permission[] {
  return ROLE_PERMISSIONS[role] ?? [];
}

/**
 * Human-readable role labels.
 */
export const ROLE_LABELS: Record<Role, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  ad_ops: 'Ad Operations',
  sales: 'Sales',
  viewer: 'Viewer',
  demo_user: 'Demo User',
};

/**
 * Role hierarchy (higher = more permissions).
 */
export const ROLE_HIERARCHY: Record<Role, number> = {
  super_admin: 5,
  admin: 4,
  ad_ops: 3,
  sales: 2,
  viewer: 1,
  demo_user: 1,
};

/**
 * Check if roleA has higher or equal privilege than roleB.
 */
export function roleAtLeast(roleA: Role, roleB: Role): boolean {
  return ROLE_HIERARCHY[roleA] >= ROLE_HIERARCHY[roleB];
}

/**
 * Navigation items visible to each role.
 */
export interface NavItem {
  label: string;
  path: string;
  icon: string;
  requiredRole?: Role;
  requiredPermission?: { resource: string; action: Action };
}

/** A group of related nav items — one workflow group in the shell (#683). */
export interface NavItemGroup {
  label: string;
  icon: string;
  items: NavItem[];
  /**
   * Short label for the top workflow-group bar, where horizontal room is tight
   * ("Analytics & Reporting" → "Analytics"). Falls back to {@link label}.
   */
  tab?: string;
  /** Icon for the top-bar tab. Falls back to {@link icon}. */
  tabIcon?: string;
  /**
   * When true, demo_user sees this group as a visible but non-navigable label
   * (to show these features exist without granting access).
   */
  demoVisible?: boolean;
}

/** A top-level navigation section: either a standalone item or a group. */
export type NavSection = { type: 'item'; item: NavItem } | { type: 'group'; group: NavItemGroup };

/**
 * The approved information architecture (#683, W2) — seven tabs since the Developer group folded
 * into Administration (#1030, 2026-09-29).
 *
 * Order is the top-bar tab order. Standalone items (Dashboard, Session
 * Simulator) are their own tabs and show no contextual sidebar — the surface
 * takes the full width. Groups with only one **visible** child collapse to a
 * direct tab, so no role is ever shown a one-item menu (see {@link buildNavTabs}).
 */
export const NAV_STRUCTURE: NavSection[] = [
  {
    type: 'item',
    item: {
      label: 'Dashboard',
      path: '/dashboard',
      icon: 'grid',
      requiredPermission: { resource: 'dashboard', action: 'read' },
    },
  },
  {
    type: 'item',
    item: {
      label: 'Session Simulator',
      path: '/demo',
      icon: 'play',
      requiredPermission: { resource: 'demo_module', action: 'read' },
    },
  },
  {
    type: 'group',
    group: {
      label: 'Strategy Studio',
      icon: 'sliders',
      tabIcon: 'sliders',
      items: [
        {
          label: 'Demand Sources',
          path: '/demand-sources',
          icon: 'database',
          requiredPermission: { resource: 'demand_sources', action: 'read' },
        },
        {
          label: 'Req Strategies',
          path: '/req-strategies',
          icon: 'refresh',
          requiredPermission: { resource: 'req_strategies', action: 'read' },
        },
        {
          label: 'Opt Strategies',
          path: '/opt-strategies',
          icon: 'trending-up',
          requiredPermission: { resource: 'opt_strategies', action: 'read' },
        },
        {
          label: 'Rule Sets',
          path: '/rule-sets',
          icon: 'filter',
          requiredPermission: { resource: 'rule_sets', action: 'read' },
        },
        {
          label: 'Selectors',
          path: '/strategy-selectors',
          icon: 'git-branch',
          requiredPermission: {
            resource: 'strategy_selectors',
            action: 'read',
          },
        },
      ],
    },
  },
  {
    type: 'group',
    group: {
      label: 'Authoring Library',
      icon: 'book-open',
      tabIcon: 'book-open',
      items: [
        {
          label: 'Pixels',
          path: '/pixels',
          icon: 'radio',
          requiredPermission: { resource: 'pixels', action: 'read' },
        },
        {
          label: 'Expression Library',
          path: '/expression-library',
          icon: 'braces',
          requiredPermission: {
            resource: 'expression_library',
            action: 'read',
          },
        },
        // Guidance is off the nav while it is disabled (`#1439`); `#854` replaces the page with a
        // disabled preview of prompt-driven strategies. The route stays, so a bookmark still explains.
        {
          label: 'Field Catalog',
          path: '/field-catalog',
          icon: 'book-open',
          requiredPermission: { resource: 'field_catalog', action: 'read' },
        },
        {
          // Where an operator expects to find "system" types — beside the field library rather
          // than under Demand Sources (`#1090`, `D31`). A template is an authoring artifact of the
          // type it templates, so it belongs to the library, not to one type's page.
          label: 'Templates',
          path: '/templates',
          icon: 'layers',
          requiredPermission: { resource: 'demand_sources', action: 'read' },
        },
      ],
    },
  },
  {
    type: 'group',
    group: {
      label: 'Diagnostics',
      icon: 'activity',
      tabIcon: 'activity',
      items: [
        {
          label: 'Sessions',
          path: '/sessions',
          icon: 'activity',
          requiredPermission: { resource: 'sessions', action: 'read' },
        },
        {
          label: 'Metrics Console',
          path: '/metrics-console',
          icon: 'terminal',
          requiredPermission: { resource: 'metrics_console', action: 'read' },
        },
        {
          // Renamed from "Request Tracer" (#819): it traces system actions by
          // correlationId, not ad requests. Route/key deliberately unchanged.
          label: 'Action Tracer',
          path: '/request-tracer',
          icon: 'search',
          requiredPermission: { resource: 'request_tracer', action: 'read' },
        },
        {
          label: 'Pipeline Diagnostics',
          path: '/pipeline-diagnostics',
          icon: 'git-commit',
          requiredPermission: {
            resource: 'pipeline_diagnostics',
            action: 'read',
          },
        },
        {
          label: 'Error Logs',
          path: '/error-logs',
          icon: 'alert-triangle',
          requiredPermission: { resource: 'error_logs', action: 'read' },
        },
        {
          label: 'Alert Subscriptions',
          path: '/alerts',
          icon: 'bell',
          requiredPermission: {
            resource: 'alert_subscriptions',
            action: 'read',
          },
        },
      ],
    },
  },
  {
    type: 'group',
    group: {
      label: 'Analytics & Reporting',
      icon: 'bar-chart',
      tab: 'Analytics',
      tabIcon: 'bar-chart',
      items: [
        {
          label: 'Analytics',
          path: '/analytics',
          icon: 'bar-chart',
          requiredPermission: { resource: 'analytics', action: 'read' },
        },
        {
          label: 'Metrics Export',
          path: '/metrics-export',
          icon: 'download',
          requiredPermission: { resource: 'metrics_export', action: 'read' },
        },
      ],
    },
  },
  {
    type: 'group',
    group: {
      label: 'Administration',
      icon: 'settings',
      tab: 'Admin',
      tabIcon: 'settings',
      demoVisible: true,
      items: [
        {
          label: 'Configuration',
          path: '/config',
          icon: 'settings',
          requiredPermission: { resource: 'config', action: 'read' },
        },
        {
          label: 'Users',
          path: '/users',
          icon: 'users',
          requiredPermission: { resource: 'users', action: 'read' },
        },
        {
          label: 'Services',
          path: '/services',
          icon: 'network',
          requiredPermission: { resource: 'services', action: 'read' },
        },
        {
          label: 'Audit Log',
          path: '/audit',
          icon: 'clipboard',
          requiredPermission: { resource: 'audit', action: 'read' },
        },
        {
          label: 'System Backup',
          path: '/system-backup',
          icon: 'archive',
          requiredRole: 'admin' as Role,
          requiredPermission: { resource: 'system_backup', action: 'manage' },
        },
        // Moved from a one-item Developer group when Webhooks was removed (#1030, Todd 2026-09-29),
        // "for now": the nav review may place it with documentation and the field/template library.
        {
          label: 'API Explorer',
          path: '/api-explorer',
          icon: 'code',
          requiredPermission: { resource: 'api_explorer', action: 'read' },
        },
      ],
    },
  },
];

/**
 * Flat list of all nav items (derived from NAV_STRUCTURE).
 * Used for page title resolution and other consumers that need a flat list.
 */
export const NAV_ITEMS: NavItem[] = NAV_STRUCTURE.flatMap((section) =>
  section.type === 'item' ? [section.item] : section.group.items,
);

/**
 * Is a nav item visible to a user? The single implementation shared by every
 * nav surface (top bar, contextual sidebar, mobile drawer, command palette) so
 * a surface can never appear in one and not another (#683 D3).
 *
 * Note it keys off `requiredPermission` only — `requiredRole` is metadata that
 * the sidebar has never enforced, and route guards own real access control.
 * Changing that here would silently remove surfaces, so it stays as-is.
 */
export function isNavItemVisible(item: NavItem, can: (resource: string, action: Action) => boolean): boolean {
  if (!item.requiredPermission) return true;
  return can(item.requiredPermission.resource, item.requiredPermission.action);
}

/**
 * A top-bar tab, resolved for one user.
 *
 * - `direct` — navigates straight to a surface, no contextual sidebar. Covers
 *   standalone items **and** groups where this user can see only one item, which
 *   is how the old sidebar rendered a single visible child. No role is ever
 *   shown a one-item menu.
 * - `group` — two or more visible surfaces; opens the contextual sidebar.
 * - `locked` — a `demoVisible` group with nothing visible: shown disabled so the
 *   demo persona can see the capability exists (the old sidebar's ghost header).
 */
export type NavTab =
  | { kind: 'direct'; key: string; label: string; icon: string; item: NavItem }
  | {
      kind: 'group';
      key: string;
      label: string;
      icon: string;
      group: NavItemGroup;
      items: NavItem[];
    }
  | { kind: 'locked'; key: string; label: string; icon: string };

/**
 * Project {@link NAV_STRUCTURE} into the tabs one user actually sees.
 *
 * `showLocked` reproduces the old sidebar's ghost group headers for demo_user;
 * every other caller leaves it off and simply gets nothing for empty groups.
 */
export function buildNavTabs(
  can: (resource: string, action: Action) => boolean,
  options: { showLocked?: boolean } = {},
): NavTab[] {
  const tabs: NavTab[] = [];

  for (const section of NAV_STRUCTURE) {
    if (section.type === 'item') {
      if (!isNavItemVisible(section.item, can)) continue;
      tabs.push({
        kind: 'direct',
        key: section.item.path,
        label: section.item.label,
        icon: section.item.icon,
        item: section.item,
      });
      continue;
    }

    const group = section.group;
    const items = group.items.filter((item) => isNavItemVisible(item, can));

    if (items.length === 0) {
      if (options.showLocked && group.demoVisible) {
        tabs.push({
          kind: 'locked',
          key: `locked:${group.label}`,
          label: group.tab ?? group.label,
          icon: group.tabIcon ?? group.icon,
        });
      }
      continue;
    }

    if (items.length === 1) {
      tabs.push({
        kind: 'direct',
        key: items[0].path,
        label: items[0].label,
        icon: items[0].icon,
        item: items[0],
      });
      continue;
    }

    tabs.push({
      kind: 'group',
      key: `group:${group.label}`,
      label: group.tab ?? group.label,
      icon: group.tabIcon ?? group.icon,
      group,
      items,
    });
  }

  return tabs;
}

/** The tab that owns a pathname, plus the item itself (for breadcrumbs/active state). */
export function findNavLocation(tabs: NavTab[], pathname: string): { tab: NavTab; item: NavItem } | undefined {
  const matches = (item: NavItem) => pathname === item.path || pathname.startsWith(item.path + '/');

  for (const tab of tabs) {
    if (tab.kind === 'direct' && matches(tab.item)) return { tab, item: tab.item };
    if (tab.kind === 'group') {
      const item = tab.items.find(matches);
      if (item) return { tab, item };
    }
  }
  return undefined;
}
